import { useEffect, useState, type FormEvent } from 'react';
import { api, BladApi, type Kontrahent, type Sesja } from '../api';
import { nip as sformatujNip } from '../format';
import { useIdOperacji } from '../operacje';
import { idz } from '../router';
import { IkonaPlus } from '../components/Icons';
import { NaglowekWidoku } from '../components/Shell';
import {
  Karta,
  Komunikat,
  PoleTekstowe,
  Przycisk,
  PrzyciskMarki,
  Pusto,
  Tag,
} from '../components/ui';

const NA_STRONE = 50;

/**
 * Kontrahenci. Docelowo lustro Subiekta (CLAUDE.md §4), ale agent powstaje w Etapie 6 —
 * do tego czasu biuro zakłada ich tutaj, bo bez kontrahenta nie ma karty urządzenia.
 */
export function CustomersView({ sesja, szukane = '' }: { sesja: Sesja; szukane?: string }) {
  const mozeDodawac = sesja.permissions.includes('customer.write');

  const [kontrahenci, setKontrahenci] = useState<Kontrahent[] | null>(null);
  const [ile, setIle] = useState(0);
  const [blad, setBlad] = useState<string | null>(null);
  const [dodawanie, setDodawanie] = useState(false);
  const [odswiez, setOdswiez] = useState(0);

  useEffect(() => {
    let aktualne = true;
    setBlad(null);

    api
      .kontrahenci({ q: szukane, limit: NA_STRONE })
      .then((wynik) => {
        if (!aktualne) return;
        setKontrahenci(wynik.kontrahenci);
        setIle(wynik.ile);
      })
      .catch((error: unknown) => {
        if (aktualne) {
          setBlad(error instanceof BladApi ? error.message : 'Nie udało się wczytać listy.');
        }
      });

    return () => {
      aktualne = false;
    };
  }, [szukane, odswiez]);

  return (
    <>
      <NaglowekWidoku
        tytul="Kontrahenci"
        podtytul={szukane ? `Szukanie: ${szukane}` : `${ile} w kartotece · źródłem prawdy jest Subiekt`}
        akcje={
          mozeDodawac && !dodawanie ? (
            <PrzyciskMarki
              type="button"
              onClick={() => setDodawanie(true)}
              ikona={<IkonaPlus rozmiar={16} />}
            >
              Dodaj kontrahenta
            </PrzyciskMarki>
          ) : undefined
        }
      />

      {dodawanie && (
        <FormularzKontrahenta
          onZapisano={() => {
            setDodawanie(false);
            setOdswiez((n) => n + 1);
          }}
          onAnuluj={() => setDodawanie(false)}
        />
      )}

      {blad && (
        <div className="mb-14">
          <Komunikat tresc={blad} />
        </div>
      )}

      <Karta wypelnienie={false}>
        {kontrahenci === null && <Pusto tresc="Wczytywanie…" />}

        {kontrahenci !== null && kontrahenci.length === 0 && (
          <Pusto
            tresc={szukane ? 'Nic nie pasuje do tego zapytania.' : 'Nie ma jeszcze kontrahentów.'}
          />
        )}

        {kontrahenci && kontrahenci.length > 0 && (
          <div className="overflow-x-auto">
            <table className="tab">
              <thead>
                <tr>
                  <th>Kontrahent</th>
                  <th>NIP</th>
                  <th>Telefon</th>
                  <th>Osoba kontaktowa</th>
                  <th>Maszyny</th>
                  <th>Źródło</th>
                </tr>
              </thead>
              <tbody>
                {kontrahenci.map((kontrahent) => (
                  <tr
                    key={kontrahent.id}
                    data-klikalny=""
                    tabIndex={0}
                    role="link"
                    title="Pokaż maszyny tego kontrahenta"
                    onClick={() => idz(`/urzadzenia?q=${encodeURIComponent(kontrahent.nazwa)}`)}
                    onKeyDown={(zdarzenie) => {
                      if (zdarzenie.key === 'Enter' || zdarzenie.key === ' ') {
                        zdarzenie.preventDefault();
                        idz(`/urzadzenia?q=${encodeURIComponent(kontrahent.nazwa)}`);
                      }
                    }}
                  >
                    <td>
                      <div className="t-body-strong text-n-900">{kontrahent.nazwa}</div>
                      {kontrahent.adres && (
                        <div className="t-small text-n-600">{kontrahent.adres}</div>
                      )}
                    </td>
                    <td className="t-mono-data whitespace-nowrap text-n-700">
                      {sformatujNip(kontrahent.nip) || '—'}
                    </td>
                    <td className="t-mono-data whitespace-nowrap text-n-600">
                      {kontrahent.telefon ?? '—'}
                    </td>
                    <td className="t-small text-n-700">{kontrahent.osobaKontaktowa ?? '—'}</td>
                    <td className="t-mono-data text-n-700">{kontrahent.urzadzen}</td>
                    <td>{kontrahent.subiektId !== null ? <Tag>Subiekt</Tag> : <Tag>ręcznie</Tag>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Karta>
    </>
  );
}

function FormularzKontrahenta({
  onZapisano,
  onAnuluj,
}: {
  onZapisano: () => void;
  onAnuluj: () => void;
}) {
  const operacja = useIdOperacji();
  const [nazwa, setNazwa] = useState('');
  const [numerNip, setNumerNip] = useState('');
  const [adres, setAdres] = useState('');
  const [telefon, setTelefon] = useState('');
  const [osobaKontaktowa, setOsobaKontaktowa] = useState('');
  const [blad, setBlad] = useState<string | null>(null);
  const [zapis, setZapis] = useState(false);

  async function wyslij(zdarzenie: FormEvent): Promise<void> {
    zdarzenie.preventDefault();
    if (!nazwa.trim() || zapis) return;

    setZapis(true);
    setBlad(null);
    try {
      await api.utworzKontrahenta({
        clientOperationId: operacja.biezacy(),
        nazwa: nazwa.trim(),
        nip: numerNip.trim() || undefined,
        adres: adres.trim() || undefined,
        telefon: telefon.trim() || undefined,
        osobaKontaktowa: osobaKontaktowa.trim() || undefined,
      });
      operacja.zakoncz();
      onZapisano();
    } catch (error: unknown) {
      setBlad(error instanceof BladApi ? error.message : 'Nie udało się zapisać.');
      setZapis(false);
    }
  }

  return (
    <form onSubmit={wyslij} noValidate>
      <Karta tytul="Nowy kontrahent" akcje={<span className="t-small text-n-500">wymagana jest tylko nazwa</span>}>
        <div className="grid gap-13 sm:grid-cols-2">
          <PoleTekstowe
            etykieta="Nazwa"
            value={nazwa}
            onChange={(z) => setNazwa(z.target.value)}
            autoFocus
          />
          <PoleTekstowe
            etykieta="NIP"
            podpowiedz="dowolny zapis, z myślnikami lub bez"
            value={numerNip}
            onChange={(z) => setNumerNip(z.target.value)}
            autoComplete="off"
          />
          <PoleTekstowe etykieta="Adres" value={adres} onChange={(z) => setAdres(z.target.value)} />
          <PoleTekstowe
            etykieta="Telefon"
            value={telefon}
            onChange={(z) => setTelefon(z.target.value)}
          />
          <PoleTekstowe
            etykieta="Osoba kontaktowa"
            value={osobaKontaktowa}
            onChange={(z) => setOsobaKontaktowa(z.target.value)}
          />
        </div>

        {blad && (
          <div className="mt-13">
            <Komunikat tresc={blad} />
          </div>
        )}

        <div className="mt-14 flex flex-wrap items-center gap-13">
          <PrzyciskMarki type="submit" disabled={!nazwa.trim() || zapis}>
            {zapis ? 'Zapisywanie…' : 'Zapisz'}
          </PrzyciskMarki>
          <Przycisk type="button" onClick={onAnuluj} disabled={zapis}>
            Anuluj
          </Przycisk>
        </div>
      </Karta>
    </form>
  );
}
